import { Button } from 'react-bootstrap';
import React, { useEffect } from 'react';
import ComposerModal from '../../ComposerModal';
import { icons } from 'images';
import { getNextechLeadPayments } from 'redux/actions/nextechActions';
import { useSelector } from 'react-redux';
import { selectors } from 'selectors/selectors';
import { action } from 'lib/actionHelpers';
import mapServicesToCamelCase from 'lib/mapServicesToCamelCase';
import { formatDate } from 'lib/dateUtilities';
import useBoundForm from 'hooks/useBoundForm.js';
import sortByProp from 'lib/sortByProp';
import BoundSelect from 'components/boundForm/BoundSelect';
import { getPipelineLeadOptions } from 'lib/pipelineLeads/getPipelineLeadOptions';
import { pipelineLeadId } from 'selectors/querySelectors';
import { putServiceInterest } from 'redux/actions/leadsActions';
import setQuery from 'lib/setQuery';
import { Loader } from 'components/Loader';

const NextechPaymentSync = () => {
  const activeLeadId = useSelector(selectors.activeLeadId);
  const activeLead = useSelector(selectors.activeLead);
  const activeLeadPipelineLeads = useSelector(selectors.activeLeadPipelineLeads);
  const pipelineLead = useSelector(selectors.leadModalActivePipelineLead) || {};
  const activeLeadSyncPlatformCode = useSelector(selectors.activeLeadSyncPlatforms);
  const activeLeadSyncPlatformLeadSyncId = useSelector(selectors.activeLeadSyncPlatformLeadSyncId);
  const serviceProviderId = useSelector(selectors.serviceProviderId);
  const activeLeadNextechPayments = useSelector(selectors.activeLeadNextechPayments);
  const totalValue = activeLeadNextechPayments.reduce((all, item) => (item.status_id === 'active' ? (all += item.value) : (all += 0)), 0);
  const nextechPaymentsLoading = useSelector((state) => state.leadModal.nextechPaymentsLoading);
  const pipelines = useSelector(selectors.serviceProviderPipelines);

  const initialServices = mapServicesToCamelCase(pipelineLead.service_interests, serviceProviderId);
  const pipelineLeadOptions = (getPipelineLeadOptions(pipelines, activeLead.pipeline_leads) || []).map((n) => ({ ...n, pipelineLeadId: n.value }));

  const form = useBoundForm({
    initialState: {
      services: initialServices,
      serviceProviderId,
      pipelineLeadId: null,
    },
  });

  const currentlySelectedPipelineLead = () => {
    if (activeLeadPipelineLeads.length === 1) {
      return activeLeadPipelineLeads[0];
    }

    return form.state.pipelineLeadId ? activeLeadPipelineLeads.find((item) => item.id === form.state.pipelineLeadId) || {} : {};
  };

  const currentlySelectedService = () => {
    // Only one pipeline lead with one service interest
    if (activeLeadPipelineLeads.length === 1 && (activeLeadPipelineLeads[0].service_interests || []).length === 1) {
      return (activeLeadPipelineLeads[0].service_interests || []).length ? activeLeadPipelineLeads[0].service_interests[0] : {};
    }
    return form.state.selectedService ? form.state.selectedService : {};
  };

  const currentlySelectedServiceValue = (currentlySelectedService() || {}).service_value;
  const noServiceSelectedMultiplePipelineLeads = (activeLeadPipelineLeads || []).length > 1 && !form.state.selectedService;
  const noServiceSelectedMultipleServiceInterestsOneServiceOption = (activeLeadPipelineLeads || []).length === 1 && (activeLeadPipelineLeads[0].service_interests || []).length > 1 && !form.state.selectedService;
  const noPipelineLeadSelected = (activeLeadPipelineLeads || []).length > 1 && !form.state.pipelineLeadId;
  const disabled = noServiceSelectedMultiplePipelineLeads || noPipelineLeadSelected || noServiceSelectedMultipleServiceInterestsOneServiceOption || nextechPaymentsLoading || Math.sign(totalValue) === -1;

  const priceDifference = () => {
    if (currentlySelectedServiceValue && totalValue) {
      const isNegative = Math.sign(totalValue);
      const currentlySelectedServiceValueInt = parseInt(currentlySelectedServiceValue);
      const returnValue = isNegative === -1 ? Math.abs(currentlySelectedServiceValueInt + totalValue) : Math.abs(currentlySelectedServiceValueInt - totalValue);
      return returnValue;
    }
    return null;
  };
  const difference = priceDifference();
  const handlePaymentClick = (payment) => {
    action('UPDATE_NEXTECH_PAYMENT_STATUS_SUCCESS', { ...payment, lead_id: activeLeadId });
  };
  const handlePipelineLeadChange = (pipelineLeadId) => {
    form.setState({
      ...form.state,
      pipelineLeadId,
    });
  };
  const handleSyncPayments = () => {
    const service = currentlySelectedService();
    const adjustedServiceValue = totalValue.toString();
    const { id } = currentlySelectedPipelineLead();
    const payload = { service_interest_id: service.id, service_offered_id: service.service_offered_id, service_value: adjustedServiceValue };
    putServiceInterest(id, payload, 'edit');
  };
  const handleGetNextechLeadPayments = () => {
    if (activeLeadSyncPlatformLeadSyncId && activeLeadSyncPlatformCode) {
      action('nextechPaymentsLoading', true);
      getNextechLeadPayments(activeLeadSyncPlatformLeadSyncId, activeLeadSyncPlatformCode, () => {
        action('nextechPaymentsLoading', false);
      });
    }
  };

  useEffect(handleGetNextechLeadPayments, [activeLeadSyncPlatformLeadSyncId, activeLeadSyncPlatformCode]);

  const handleServicesChange = (serviceId) => {
    const service = (currentlySelectedPipelineLead() || {}).service_interests.find((n) => n.id === serviceId) || {};
    if (!form.state.amount && !form.state.multiplier) {
      form.setState({
        ...form.state,
        selectedService: service,
        serviceId: service.id,
      });
    }
  };

  const services = (currentlySelectedPipelineLead() || {}).service_interests;

  const serviceOptions = (services || []).map((n) => ({ value: n.id, label: n.service_name })).sort(sortByProp('label'));

  if (!(activeLeadNextechPayments || []).length && nextechPaymentsLoading === false) {
    return (
      <ComposerModal className={`lead-sync-composer nextech-payment-composer`} title={'Nextech Payment Sync'}>
        <div className={'composer-content'}>
          <div className="nextech-payment-header">
            <p className="headline">No payments detected</p>
          </div>
        </div>
        <div className={'composer-footer'}>
          <div className={'left'}>
            <Button variant={'secondary'} onClick={() => setQuery({ ia: null, ty: null })}>
              Close
            </Button>
          </div>
        </div>
      </ComposerModal>
    );
  }

  if (nextechPaymentsLoading === true) {
    return (
      <ComposerModal className={`lead-sync-composer nextech-payment-composer`} title={'Nextech Payment Sync'}>
        <div className={'composer-content nextech-loading'}>
          <Loader size={'sm'} container={true} type={'pulseCircle'} />
        </div>
      </ComposerModal>
    );
  }

  return (
    <ComposerModal className={`lead-sync-composer nextech-payment-composer`} title={'Nextech Payment Sync'}>
      <div className={'composer-content'}>
        <div className="nextech-payment-header">
          <p className="headline">We detected payments in Nextech</p>
          <p className="sub">Select the payments you would like to sync to each service</p>
        </div>
        {(activeLeadPipelineLeads || []).length > 1 ? <BoundSelect formId={form.id} id={'pipelineLeadId'} placeholder={'Pipeline Leads'} options={pipelineLeadOptions} onChange={handlePipelineLeadChange} /> : null}

        {((currentlySelectedPipelineLead() || {}).service_interests || []).length > 1 ? <BoundSelect formId={form.id} id={'serviceId'} placeholder={'Services Offered'} options={serviceOptions} onChange={handleServicesChange} /> : null}
        {activeLeadNextechPayments.map((n, i) => {
          return (
            <div key={n.id} onClick={(e) => handlePaymentClick(n)} className={`nextech-sync-payments payment-row ${n.status_id === 'active' ? 'active' : 'inactive'}`}>
              <div className="nextech-sync-payments-label payment-row">
                <img height={14} width={14} src={icons['icon-success-green-checkmark']} alt={'Checkmark'} />
                <p>
                  {n.label} {n.date ? <span>({formatDate(n.date)})</span> : null}
                </p>
              </div>
              <div className="nextech-sync-payments-value">
                <p>${n.value.toLocaleString()}</p>
              </div>
            </div>
          );
        })}
        <div style={{ borderBottom: 'none' }} className="nextech-sync-payments">
          <div className="nextech-sync-payments-label">
            <p>Nextech value</p>
          </div>
          <div className="nextech-sync-payments-value">
            <p>${totalValue.toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className={'composer-footer'}>
        <div className={'left'}>
          <Button variant={'primary'} disabled={disabled} onClick={() => handleSyncPayments()}>
            Update
          </Button>
          <Button variant={'secondary'} onClick={() => setQuery({ ia: null, ty: null })}>
            Close
          </Button>
        </div>
        <div className={'right nextech-sync-composer-footer'}>
          <p>{currentlySelectedServiceValue ? `Leadloop value: $${currentlySelectedServiceValue}` : null}</p>
          <p className="difference">{difference ? `Difference: $${difference}` : null}</p>
        </div>
      </div>
    </ComposerModal>
  );
};

export default NextechPaymentSync;
